import { memo, useCallback, useContext, useMemo } from "react";
import "../../css/GlobalStyles.css";
import { useSelector, useDispatch } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import { MARKIT_CONTACT_US, MARKITAI } from "@markit/common.utils";
import { useNavigate } from "../../hooks/useNavigate";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { Icon } from "@iconify/react";
import {
  LandingPageNavigationId,
  NavigationId,
} from "../../navigation/AppParamList";
import LandingPagesWrapper from "../../components/LandingPage/LandingPagesWrapper";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import { useWindowResize } from "../../hooks/useWindowResize";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import LabelButton from "../../components/Buttons/LabelButton";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { MixpanelContext } from "../../context/AnalyticsService";
import LandingPageDividedSection from "../../components/LandingPage/LandingPageDividedSection";
import TextingThatFeelsHumanFold from "../../components/LandingPage/TextingThatFeelsHumanFold";
import { FoldHeaderText } from "../../components/LandingPage/SubComponents/FoldHeaderText";
import CarouselContainer, {
  CarouselItem,
} from "../../components/AnimationContainers/CarouselContainer";
import {
  JessicaBlumMembership,
  JessicaBlumMembershipMobile,
  ZaneCarterMembership,
  ZaneCarterMembershipMobile,
  AlexJohnsonMembership,
  AlexJohnsonMembershipMobile,
} from "../../assets/LandingPage/MembershipCarousel/membershipCarouselIndex";
import HostAndSellProducts from "../../assets/LandingPage/HostAndSellProducts.png";
import OfferExclusiveMemberships from "../../assets/LandingPage/OfferExclusiveMemberships.png";
import PricingThatScalesWithYou from "../../assets/LandingPage/PricingThatScalesWithYou.png";

const MonetizationProductPage = memo(function MonetizationProductPageFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);

  useSlideUpAnimation();
  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    button: {
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    },

    foldContainer: {
      paddingBlock: isDesktopSize ? 80 : 60,
      backgroundImage: "linear-gradient(to bottom, #141516 0%, #000000 31%)",
    },

    largeImage: {
      width: "100%",
      height: "auto",
    },

    safeView: {
      paddingInline: 20,
    },
  };

  const getStartedOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Get Started", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN) {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      } else {
        navigate(NavigationId.WELCOME);
      }
    },
    [
      accountData.uid,
      accountData.username,
      dispatch,
      loggedIn,
      navigate,
      mixpanel,
    ]
  );

  const pricingOnPress = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Website: Navigate To Pricing Product Page", {
        distinct_id: accountData.uid,
        source: "Monetization Page",
      });
    }
    navigate(LandingPageNavigationId.PRICING);
  }, [accountData.uid, mixpanel, navigate]);

  const supportOnPress = useCallback(() => {
    if (mixpanel) {
      mixpanel.track("Website: Home Pressed Contact Us", {
        distinct_id: accountData.uid,
        is_logged_in: loggedIn === LoginState.LOGGED_IN,
        btn_source: "Monetization Page Pricing Fold",
      });
    }
    window.open(MARKIT_CONTACT_US);
  }, [accountData.uid, loggedIn, mixpanel]);

  const carouselItems: CarouselItem[] = [
    {
      image: isDesktopSize
        ? JessicaBlumMembership
        : JessicaBlumMembershipMobile,
      title: "",
      width: 884, // padding helps set width for mobile view
    },
    {
      image: isDesktopSize ? ZaneCarterMembership : ZaneCarterMembershipMobile,
      title: "",
      width: 884,
    },
    {
      image: isDesktopSize
        ? AlexJohnsonMembership
        : AlexJohnsonMembershipMobile,
      title: "",
      width: 884,
    },
  ];

  return (
    <LandingPagesWrapper
      helmetData={{
        title: "Monetization | Markit AI",
        metaContent:
          "Sell merchandise, products, and offer paid memberships. Build your business and get paid to text with Markit AI.",
        link: MARKITAI + "/monetization",
      }}
      noRedirect
    >
      {/* Fold 1 - Monetize Effortlessly */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{
          ...styles.safeView,
          paddingTop: isDesktopSize ? 100 : 60,
          paddingBottom: isDesktopSize ? 80 : 60,
        }}
      >
        <div
          className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
          style={{
            gap: 24,
            textAlign: isDesktopSize ? "center" : "left",
          }}
        >
          <LabelButton
            text="Get paid to text"
            color={Colors.PINK2}
            backgroundColor={Colors.PINK2 + "1a"}
            icon={
              <Icon
                icon="ion:wallet"
                height={14}
                style={{ color: Colors.PINK2 }}
              />
            }
            borderColor={Colors.PINK2}
            containerStyles={{ fontSize: 16, fontWeight: 500 }}
          />
          <FoldHeaderText isDesktopSize={isDesktopSize}>
            Monetize effortlessly
          </FoldHeaderText>
          <span className="landingPageHeaderSubtext">
            Sell merchandise, products, offer paid memberships,
            {isDesktopSize ? <br /> : " "}and build your business with
            {isDesktopSize ? " " : <br />}Markit AI.
          </span>
        </div>
        <div className="slide-up" style={{ paddingBlock: 60 }}>
          <CarouselContainer
            carouselItems={carouselItems}
            containerStyles={{ maxWidth: 884, gap: 14 }}
            dark
            enableSwipe
          />
        </div>
        <LandingPageDividedSection
          isDesktopSize={isDesktopSize}
          leftSection={{
            header: "Host and sell products",
            subtext:
              "Create newsletter-style posts, sell merchandise, host paid ticketed events, webinars, PDFs and more. All on Markit.",
            image: HostAndSellProducts,
          }}
          rightSection={{
            header: "Offer exclusive memberships",
            subtext:
              "Offer exclusive benefits, content, 1:1 consulting, discounts, and charge monthly membership fees from paid subscribers.",
            image: OfferExclusiveMemberships,
          }}
          verticalDividerHeight={733}
        />
      </div>
      {/* Fold 2 - Pricing That Scales With You */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, margin: "0 auto" }}>
          <div
            className={isDesktopSize ? "RowNormal" : "ColumnNormal"}
            style={{ gap: isDesktopSize ? 149 : 60 }}
          >
            <div className="ColumnNormal">
              <LabelButton
                text="Pricing"
                color={Colors.BLUE5}
                backgroundColor={Colors.BLUE5 + "1a"}
                icon={
                  <Icon
                    icon="ion:pricetag"
                    height={14}
                    style={{ color: Colors.BLUE5 }}
                  />
                }
                borderColor={Colors.BLUE5}
                containerStyles={{ fontSize: 16, fontWeight: 500 }}
              />
              <div className="ColumnNormal" style={{ gap: 30 }}>
                <FoldHeaderText isDesktopSize={isDesktopSize}>
                  Pricing That
                  <br />
                  Scales With You
                </FoldHeaderText>
                <span className="landingPageHeaderSubtext">
                  Markit is free to use. Upgrade to Markit+ to explore
                  additional features and unlock unlimited texting. Contact
                  support to learn more.
                </span>
                <div
                  className="AlignedRow"
                  style={{ gap: isDesktopSize ? 14 : 10 }}
                >
                  <RectangleButton
                    buttonLabel="Pricing"
                    onPress={pricingOnPress}
                    altColor={Colors.WHITE}
                    altTextColor={Colors.BLACK}
                    iconRight={
                      <Icon
                        icon="ion:chevron-forward"
                        height={16}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                    containerStyles={styles.button}
                  />
                  <RectangleButton
                    buttonLabel="Contact Support"
                    onPress={supportOnPress}
                    altColor={Colors.BLACK}
                    altTextColor={Colors.WHITE}
                    iconRight={
                      <Icon
                        icon="ion:chevron-forward"
                        height={16}
                        style={{ color: Colors.WHITE }}
                      />
                    }
                    containerStyles={{
                      ...styles.button,
                      border: `0.5px solid ${Colors.BLACK1}`,
                    }}
                  />
                </div>
              </div>
            </div>
            <img
              src={PricingThatScalesWithYou}
              alt="Follower network growth"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
                maxWidth: 545,
              }}
            />
          </div>
        </div>
      </div>
      {/* Fold 3 - Texting that feels Human */}
      <TextingThatFeelsHumanFold
        isDesktopSize={isDesktopSize}
        getStartedOnPress={getStartedOnPress}
      />
    </LandingPagesWrapper>
  );
});

export default MonetizationProductPage;
